/**
 * je-analytics.js — Shared storefront analytics client for Journey's End Games.
 *
 * Loaded on all Shopify theme pages via theme.liquid.
 * Exposes window.JEAnalytics for page-level tracking.
 *
 * Configuration:
 *   window.JE_ANALYTICS_ENDPOINT — POST target for event batches (set in theme.liquid)
 */
(function () {
  "use strict";

  // ---------------------------------------------------------------------------
  // UUID generation
  // ---------------------------------------------------------------------------

  function uuidv4() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    // Fallback for older browsers using crypto.getRandomValues
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, function (c) {
      return (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16);
    });
  }

  // ---------------------------------------------------------------------------
  // Identity: Session ID
  // ---------------------------------------------------------------------------

  var _sessionId = null;

  function getSessionId() {
    if (_sessionId) return _sessionId;

    _sessionId = sessionStorage.getItem("je:session-id");
    if (_sessionId) return _sessionId;

    // Compatibility: adopt existing deck-builder session if present
    var legacy = sessionStorage.getItem("dbui:session-id");
    if (legacy) {
      _sessionId = legacy;
      sessionStorage.setItem("je:session-id", _sessionId);
      return _sessionId;
    }

    _sessionId = uuidv4();
    sessionStorage.setItem("je:session-id", _sessionId);
    return _sessionId;
  }

  // ---------------------------------------------------------------------------
  // Identity: Visitor ID (persists across sessions)
  // ---------------------------------------------------------------------------

  var _visitorId = null;

  function getVisitorId() {
    if (_visitorId) return _visitorId;

    try {
      _visitorId = localStorage.getItem("je:visitor-id");
      if (!_visitorId) {
        _visitorId = uuidv4();
        localStorage.setItem("je:visitor-id", _visitorId);
      }
    } catch (_) {
      // localStorage unavailable (private browsing, storage quota, etc.)
      _visitorId = null;
    }
    return _visitorId;
  }

  // ---------------------------------------------------------------------------
  // Identity: Page Load ID (fresh per page render)
  // ---------------------------------------------------------------------------

  var PAGE_ID = uuidv4();
  var PAGE_STARTED_AT =
    typeof performance !== "undefined" &&
    typeof performance.timeOrigin === "number" &&
    isFinite(performance.timeOrigin) &&
    performance.timeOrigin > 0
      ? Math.round(performance.timeOrigin)
      : Date.now();

  function getPageId() {
    return PAGE_ID;
  }

  // ---------------------------------------------------------------------------
  // UTM collection — parsed once per page load
  // ---------------------------------------------------------------------------

  function parseUtmParams() {
    var params = {};
    try {
      var search = new URLSearchParams(location.search);
      var keys = [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "utm_id",
        "utm_source_platform",
        "utm_placement",
        "adset_id",
        "ad_id",
        "fbclid",
        "gclid",
        "gbraid",
        "wbraid",
        "ref",
        "subId"
      ];
      keys.forEach(function (k) {
        var v = search.get(k);
        if (v) params[k] = v;
      });
    } catch (_) {
      // URLSearchParams not available or location inaccessible
    }
    return Object.keys(params).length ? params : null;
  }

  var UTM = parseUtmParams();

  // ---------------------------------------------------------------------------
  // Partner referral capture — ?ref= from the landing URL
  //
  // Register generic landing context server-side, then persist its UUID for
  // je-track-cart.liquid and the deck-builder UI to stamp onto the cart. Last
  // successful capture wins. The orders/create webhook is the sole authority
  // for the attribution window and consuming each ID once.
  // ---------------------------------------------------------------------------

  var PARTNER_REF_KEY = "je:partner-ref";
  var PARTNER_REF_CODE_RE = /^[A-Za-z0-9_-]{2,64}$/;
  var PARTNER_SUB_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/;
  var PARTNER_ATTRIBUTION_ID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // Referral cart attributes survive checkout. Update them as soon as a new
  // tagged landing is captured so accelerated checkout / "Buy it now" paths
  // cannot reuse the previous landing's already-consumed attribution ID.
  // je-track-cart.liquid repeats this before AJAX add-to-cart as a safeguard.
  function syncPartnerRefToCart(partnerRef) {
    if (typeof fetch !== "function") return;
    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attributes: {
          // New captures resolve entirely through this UUID. Legacy values are
          // retained only for pre-deploy localStorage entries whose UUID has
          // not yet been registered server-side.
          _je_partner_ref: partnerRef.code || null,
          _je_partner_landing_ts: partnerRef.ts ? String(partnerRef.ts) : null,
          _je_partner_attribution_id: partnerRef.attributionId
        }
      })
    }).catch(function () {
      // The pre-add-to-cart stamp remains as a retry path.
    });
  }

  function hasMatchingSharedDeckCapture(landingPath) {
    try {
      var current = JSON.parse(localStorage.getItem(PARTNER_REF_KEY) || "null");
      return !!(
        current &&
        current.kind === "shared_deck" &&
        current.landingPath === landingPath &&
        typeof current.attributionId === "string" &&
        PARTNER_ATTRIBUTION_ID_RE.test(current.attributionId)
      );
    } catch (_) {
      return false;
    }
  }

  async function capturePartnerRef() {
    if (!UTM) return;
    var code = UTM.ref;
    if (!code || !PARTNER_REF_CODE_RE.test(code)) return;
    var endpoint = window.JE_REFERRAL_ATTRIBUTION_ENDPOINT;
    if (!endpoint || typeof fetch !== "function") return;
    try {
      var landingPath = location.pathname + location.search;
      var attributionId = uuidv4();
      var response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attribution_id: attributionId,
          ref_code: code.toLowerCase(),
          sub_id: UTM.subId && PARTNER_SUB_ID_RE.test(UTM.subId) ? UTM.subId : null,
          landing_path: landingPath,
          session_id: getSessionId(),
          visitor_id: getVisitorId(),
          landing_ts: PAGE_STARTED_AT
        })
      });
      if (!response.ok) return;
      // The Deck Builder registers partner-created shared links separately.
      // If that more-specific capture completed while this generic request was
      // in flight, do not let the generic response overwrite it.
      if (hasMatchingSharedDeckCapture(landingPath)) return;
      var partnerRef = { attributionId: attributionId.toLowerCase() };
      localStorage.setItem(PARTNER_REF_KEY, JSON.stringify(partnerRef));
      syncPartnerRefToCart(partnerRef);
    } catch (_) {
      // localStorage unavailable — referral attribution degrades gracefully
    }
  }

  // Returns the stored attribution candidate. New entries contain one UUID;
  // old entries may also carry ref/timestamp for the deployment fallback.
  function getPartnerRef() {
    try {
      var raw = localStorage.getItem(PARTNER_REF_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (
        !data ||
        typeof data.attributionId !== "string" ||
        !PARTNER_ATTRIBUTION_ID_RE.test(data.attributionId)
      ) {
        return null;
      }
      var result = { attributionId: data.attributionId.toLowerCase() };
      // Compatibility for candidates captured before referral_attributions
      // existed. Cart stamping keeps their ref/timestamp until the webhook
      // can backfill the server-side row.
      if (
        typeof data.code === "string" &&
        PARTNER_REF_CODE_RE.test(data.code) &&
        typeof data.ts === "number" &&
        isFinite(data.ts)
      ) {
        result.code = data.code;
        result.ts = data.ts;
      }
      return result;
    } catch (_) {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Engagement tracking
  // ---------------------------------------------------------------------------

  var loadTime = Date.now();
  var activeStart = document.visibilityState === "visible" ? loadTime : null;
  var accumulatedActiveMs = 0;
  var maxScrollPercent = 0;

  function getDurationMs() {
    return Date.now() - loadTime;
  }

  function getActiveMs() {
    var extra = 0;
    if (activeStart !== null) {
      extra = Date.now() - activeStart;
    }
    return accumulatedActiveMs + extra;
  }

  function onVisibilityChange() {
    if (document.visibilityState === "visible") {
      activeStart = Date.now();
    } else {
      if (activeStart !== null) {
        accumulatedActiveMs += Date.now() - activeStart;
        activeStart = null;
      }
    }
  }

  document.addEventListener("visibilitychange", onVisibilityChange);

  // Throttled scroll depth tracking (~100ms)
  var scrollThrottleTimer = null;

  function updateScrollDepth() {
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    var docHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight || 0
    );
    var winHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    var scrollable = docHeight - winHeight;
    if (scrollable > 0) {
      var pct = Math.round((scrollTop / scrollable) * 100);
      if (pct > maxScrollPercent) maxScrollPercent = pct;
    }
  }

  window.addEventListener("scroll", function () {
    if (scrollThrottleTimer) return;
    scrollThrottleTimer = setTimeout(function () {
      scrollThrottleTimer = null;
      updateScrollDepth();
    }, 100);
  }, { passive: true });

  function getEngagement() {
    return {
      duration_ms: getDurationMs(),
      active_ms: getActiveMs(),
      max_scroll_percent: maxScrollPercent
    };
  }

  // ---------------------------------------------------------------------------
  // Experiment context — je-experiments.js loads after this file, so read
  // the exposed map lazily at flush time.
  // ---------------------------------------------------------------------------

  function getExposedExperiments() {
    try {
      if (window.JEExperiments && typeof window.JEExperiments.getExposed === "function") {
        return window.JEExperiments.getExposed();
      }
    } catch (_) {
      // never block analytics on experiment state
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // Page context
  // ---------------------------------------------------------------------------

  var _pageContext = null;
  var _pageViewEmitted = false;

  function setPageContext(context) {
    _pageContext = context || {};
    if (!_pageViewEmitted) {
      _pageViewEmitted = true;
      track("page.view", Object.assign({}, _pageContext));
      scheduleEngagementCheckpoints();
    }
  }

  function getPageContext() {
    return _pageContext;
  }

  // ---------------------------------------------------------------------------
  // Event sequence counter (persisted per session)
  // ---------------------------------------------------------------------------

  var SEQ_KEY = "je:event-seq";

  function nextSeq() {
    var seq = parseInt(sessionStorage.getItem(SEQ_KEY), 10) || 0;
    seq += 1;
    sessionStorage.setItem(SEQ_KEY, String(seq));
    return seq;
  }

  // ---------------------------------------------------------------------------
  // Event queue
  // ---------------------------------------------------------------------------

  var FLUSH_INTERVAL_MS = 3000;
  var FLUSH_THRESHOLD = 20;

  var queue = [];
  var lastEventTime = 0;
  var lastFlushTime = 0;
  var engagementRetryPending = false;
  var flushTimer = null;

  // Event names that should not wait for the batch timer.
  var IMMEDIATE_FLUSH_NAMES = [
    "cart.add_success",
    "cart.add_failed",
    "add_to_cart_success",
    "add_to_cart_failed",
    "navigation.click",
    "recommendation.click",
    "recently_viewed.click"
  ];
  var IMMEDIATE_FLUSH_SUFFIXES = [
    ".add_to_cart_success",
    ".add_to_cart_failed"
  ];

  function hasSuffix(value, suffix) {
    return value.slice(value.length - suffix.length) === suffix;
  }

  function shouldFlushImmediately(name) {
    for (var i = 0; i < IMMEDIATE_FLUSH_NAMES.length; i++) {
      if (name === IMMEDIATE_FLUSH_NAMES[i]) return true;
    }
    for (var j = 0; j < IMMEDIATE_FLUSH_SUFFIXES.length; j++) {
      if (hasSuffix(name, IMMEDIATE_FLUSH_SUFFIXES[j])) return true;
    }
    return false;
  }

  function track(name, payload) {
    var event = {
      seq: nextSeq(),
      name: name,
      ts: Date.now(),
      payload: payload || {}
    };
    queue.push(event);
    lastEventTime = Date.now();

    if (queue.length >= FLUSH_THRESHOLD || shouldFlushImmediately(name)) {
      flush();
    } else {
      scheduleFlush();
    }
  }

  function scheduleFlush() {
    if (flushTimer) return;
    flushTimer = setTimeout(function () {
      flushTimer = null;
      flush();
    }, FLUSH_INTERVAL_MS);
  }

  // ---------------------------------------------------------------------------
  // Flush — send queued events to the endpoint
  // ---------------------------------------------------------------------------

  var MAX_QUEUE_SIZE = 500;

  // force=true: send an engagement-only batch even when the queue is empty
  // (used by scheduled checkpoints and on pagehide / tab hide so the latest
  // duration/active/scroll values reach page_visits without a raw event row).
  function flush(force) {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }

    var shouldForce = force || engagementRetryPending;
    if (queue.length === 0 && !shouldForce) return;

    var endpoint = window.JE_ANALYTICS_ENDPOINT;
    if (!endpoint) return;

    engagementRetryPending = false;
    var ctx = _pageContext || {};
    var batch = queue.splice(0, queue.length);
    var envelope = {
      session_id: getSessionId(),
      visitor_id: getVisitorId() || null,
      page_id: PAGE_ID,
      page_type: ctx.page_type || null,
      page_path: ctx.path || location.pathname,
      referrer: document.referrer || "",
      utm: UTM,
      engagement: getEngagement(),
      experiments: getExposedExperiments(),
      events: batch
    };

    var body = JSON.stringify(envelope);
    var sent = false;

    try {
      if (typeof navigator.sendBeacon === "function") {
        sent = navigator.sendBeacon(endpoint, body);
      }

      if (!sent && typeof fetch === "function") {
        fetch(endpoint, {
          method: "POST",
          body: body,
          headers: { "Content-Type": "text/plain" },
          keepalive: true
        }).catch(function () {
          requeueEvents(batch, shouldForce && batch.length === 0);
        });
        sent = true;
      }
    } catch (_) {
      // Never block UI
    }

    if (!sent) {
      requeueEvents(batch, shouldForce && batch.length === 0);
    } else {
      lastFlushTime = Date.now();
    }
  }

  function requeueEvents(events, retryEngagementOnly) {
    var spaceLeft = MAX_QUEUE_SIZE - queue.length;
    if (spaceLeft <= 0) return;
    var toRestore = events.slice(0, spaceLeft);
    queue.unshift.apply(queue, toRestore);
    if (retryEngagementOnly) engagementRetryPending = true;
    if (toRestore.length > 0 || retryEngagementOnly) scheduleFlush();
  }

  function noop() {}

  // ---------------------------------------------------------------------------
  // Page lifecycle: pagehide / visibility hidden
  // ---------------------------------------------------------------------------

  function onPageLeave() {
    track("page.leave", {});
    flush(true);
  }

  window.addEventListener("pagehide", onPageLeave);

  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "hidden") {
      flush(true);
    }
  });

  // ---------------------------------------------------------------------------
  // Engagement checkpoint schedule (exponential-backoff)
  // ---------------------------------------------------------------------------

  var CHECKPOINT_SCHEDULE_S = [3, 5, 7, 10, 15, 20, 30, 45, 60, 90, 120, 180, 300];
  var CHECKPOINT_SKIP_WINDOW_MS = 2000;

  function scheduleEngagementCheckpoints() {
    CHECKPOINT_SCHEDULE_S.forEach(function (delayS) {
      setTimeout(function () {
        sendEngagementCheckpoint();
      }, delayS * 1000);
    });
  }

  function sendEngagementCheckpoint() {
    if (document.visibilityState !== "visible") return;
    if (
      Date.now() - Math.max(lastEventTime, lastFlushTime) <
      CHECKPOINT_SKIP_WINDOW_MS
    ) return;
    flush(true);
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  // Eagerly initialize identities so they're available immediately
  getSessionId();
  getVisitorId();

  window.JEAnalytics = {
    getSessionId: getSessionId,
    getVisitorId: getVisitorId,
    getPageId: getPageId,
    capturePartnerRef: capturePartnerRef,
    getPartnerRef: getPartnerRef,
    setPageContext: setPageContext,
    getPageContext: getPageContext,
    track: track,
    flush: flush
  };
})();
